import classNames from 'classnames';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import { Card, Field, PageHeader } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import { formatDateTime } from '@/utils/format';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import type { MessageDetail } from './api-types';
import { CHANNELS, channelName } from './channels';
import MessagingNav from './messaging-nav';
import {
  ActionBar,
  ChannelIcon,
  ChannelList,
  ChannelPreview,
  NotFound,
  SegmentList,
  SkippedWarnings,
  errorStatus,
  fromLocalInput,
  toLocalInput,
  useAudience,
  REACH,
  linkButtonLabel,
} from './parts';

/** How each channel reaches people, for the confirmation. */
const VIA: Record<MessageDetail['channels'][number], string> = { email: 'by email', telegram: 'on Telegram', popup: 'in Owner Hub' };

/** The last look before sending (OUT-1, OUT-4, OUT-6): who gets it on which channel, how it looks there, and when it goes. */
const Review = ({ m }: { m: MessageDetail }) => {
  const navigate = useNavigate();
  const audience = useAudience(m.segment_ids, m.channels);
  const [later, setLater] = useState(m.status === 'scheduled');
  const [sendAt, setSendAt] = useState<string | null>(m.scheduled_at);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const a = audience.data;
  const noText = !m.body.trim();
  // A time gone by would send at once: never what someone scheduling means.
  const past = later && sendAt !== null && new Date(sendAt).getTime() <= Date.now();
  const blocked = noText || !m.channels.length || !m.segment_ids.length || !a || a.blocked || (later && !sendAt) || past;
  const reach = a ? m.channels.map((c) => `${a.channels[c] ?? 0} ${VIA[c]}`).join(', ') : '';
  const action = later ? 'Schedule' : 'Send now';

  const go = async () => {
    setBusy(true);
    setFailed(null);
    try {
      if (later) await http.post(`/messages/${m.id}/schedule`, { at: sendAt });
      else await http.post(`/messages/${m.id}/send`);
      navigate(`/messaging/messages/${m.id}`);
    } catch (err) {
      setFailed(
        errorStatus(err) === 422
          ? 'Prisma refused it: check the text, the channels and the segments.'
          : errorStatus(err) === 409
            ? 'This message is no longer a draft.'
            : 'That did not work. Try again.',
      );
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  // The send action: in the "When" card on a desktop, in a bar above the bottom tabs on a phone (UI-2).
  const sendAction = (
    <>
      {confirming && a ? (
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <p className="basis-full text-sm text-fg md:basis-auto">
            {later && sendAt ? `Schedule for ${formatDateTime(sendAt)}` : 'Send now'}: {reach}?
          </p>
          <button type="button" className={classNames(btn.primary, 'flex-1 md:flex-none')} disabled={busy} onClick={go}>
            Yes, {action.toLowerCase()}
          </button>
          <button type="button" className={classNames(btn.quiet, 'flex-1 md:flex-none')} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" className={classNames(btn.primary, 'w-full md:w-auto')} disabled={blocked} onClick={() => setConfirming(true)}>
          {action}
        </button>
      )}
      {!later && <p className="mt-2 text-xs text-fg-muted">It goes out within a minute.</p>}
      {failed && <p className="mt-2 text-sm text-danger">{failed}</p>}
    </>
  );

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="space-y-4 md:space-y-6">
          <Card title="Summary">
            <dl>
              <Field label="Title">{m.title}</Field>
              <Field label="Channels">
                <ChannelList channels={m.channels} />
              </Field>
              <Field label="Segments">
                <SegmentList ids={m.segment_ids} max={6} />
              </Field>
              {m.channels.includes('popup') && m.link_url && (
                <Field label="Link">
                  <span className="break-all">{m.link_url}</span> <span className="text-fg-muted">· button “{linkButtonLabel(m)}”</span>
                </Field>
              )}
            </dl>
            {noText && (
              <p className="mt-3 text-sm text-danger">
                The message has no text yet.{' '}
                <Link to={`/messaging/messages/${m.id}/edit`} className={btn.link}>
                  Add it in the editor
                </Link>
              </p>
            )}
          </Card>

          <Card title="Audience" aside={a && <span className="text-xs tabular-nums text-fg-muted">{a.people} in its segments</span>}>
            {audience.error && <p className="text-sm text-danger">The audience could not be counted.</p>}
            {!a && !audience.error && <p className="text-sm text-fg-faint">{m.segment_ids.length ? 'Counting…' : 'No segment yet.'}</p>}
            {a && (
              <>
                <dl>
                  {m.channels.map((c) => (
                    <Field key={c} label={channelName(c)}>
                      <span className="tabular-nums">{a.channels[c] ?? 0}</span>{' '}
                      {REACH[c]}
                    </Field>
                  ))}
                </dl>
                {m.channels.length > 1 && (
                  <p className="mt-2 text-xs text-fg-muted">One person can get it on several channels. Prisma does not deduplicate across channels (OUT-4).</p>
                )}
                {m.channels.includes('email') && <p className="mt-2 text-xs text-fg-muted">No email provider is chosen yet: emails are written to the log and marked Simulated.</p>}
                <SkippedWarnings audience={a} />
              </>
            )}
          </Card>

          <Card title="When">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-fg">
              <label className="inline-flex min-h-11 items-center gap-2 md:min-h-0">
                <input type="radio" name="review-when" className="h-5 w-5 accent-brand md:h-auto md:w-auto" checked={!later} onChange={() => setLater(false)} />
                Send now
              </label>
              <label className="inline-flex min-h-11 items-center gap-2 md:min-h-0">
                <input type="radio" name="review-when" className="h-5 w-5 accent-brand md:h-auto md:w-auto" checked={later} onChange={() => setLater(true)} />
                Schedule
              </label>
              {later && (
                <input
                  type="datetime-local"
                  aria-label="Send at"
                  className={classNames(field.input, 'w-full md:w-auto')}
                  min={toLocalInput(new Date().toISOString())}
                  value={toLocalInput(sendAt)}
                  onChange={(e) => setSendAt(fromLocalInput(e.target.value))}
                />
              )}
            </div>
            {later && <p className="mt-2 text-xs text-fg-muted">In your time; Prisma shows it in UTC.</p>}
            {past && <p className="mt-1 text-xs text-danger">That time has passed: pick a later one, or send now.</p>}

            <div className="mt-5 hidden border-t border-line pt-4 md:block">{sendAction}</div>
          </Card>
        </div>

        <div className="space-y-4 md:space-y-6">
          {CHANNELS.filter((c) => m.channels.includes(c.id)).map((c) => (
            <Card
              key={c.id}
              title={
                <span className="inline-flex items-center gap-1.5">
                  <ChannelIcon channel={c.id} />
                  {c.label}
                </span>
              }
              aside={<span className="hidden text-xs text-fg-faint sm:inline">{c.note}</span>}
            >
              <ChannelPreview channel={c.id} title={m.title} body={m.body} linkLabel={linkButtonLabel(m)} />
              <p className="mt-2 text-xs text-fg-faint sm:hidden">{c.note}</p>
            </Card>
          ))}
        </div>
      </div>
      <ActionBar className="mt-4 md:hidden">{sendAction}</ActionBar>
    </>
  );
};

/** Review and send one stored message (OUT-1, OUT-4, OUT-6). Only drafts and scheduled messages can go out from here. */
const ReviewPage = () => {
  const { id = '' } = useParams();
  const { data: m, error } = useApi<MessageDetail>(`/messages/${encodeURIComponent(id)}`);

  let body;
  if (error === 'Not found.') body = <NotFound what="message" back="/messaging/messages" label="Back to messages" />;
  else if (error) body = <StateMessage title="The message could not be loaded" tone="error">{error}</StateMessage>;
  else if (!m) body = <div className="h-64 animate-pulse rounded-card bg-surface shadow-e1" />;
  else if (m.status !== 'draft' && m.status !== 'scheduled') {
    body = (
      <StateMessage title="Nothing to review">
        This message is {m.status}.{' '}
        <Link to={`/messaging/messages/${m.id}`} className={btn.link}>
          Open it
        </Link>
      </StateMessage>
    );
  } else body = <Review key={m.id} m={m} />;

  return (
    <div>
      <PageHeader
        title="Review and send"
        description={m?.title}
        back={m?.status === 'draft' ? { to: `/messaging/messages/${m.id}/edit`, label: 'Back to the editor' } : { to: '/messaging/messages', label: 'Messages' }}
      >
        <MessagingNav />
      </PageHeader>
      {body}
    </div>
  );
};

export default ReviewPage;
